import { matchPath } from 'react-router-dom';
import {
  isProjectScopedProductPath,
  resolveProjectRouteScope,
} from '@/features/projects/utils/projectScopedRoutes';
import { resolveActiveContext } from '@/shared/hooks/contextResolution';
import type { AuthSession } from '@/auth/types';
import type { AppRouteDefinition } from '@/navigation/routes/routes';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

const isDetailRoutePath = (activePath: string, routeDefinitions: AppRouteDefinition[]): boolean =>
  routeDefinitions.some((route) => {
    if (!route.key.includes('detail')) return false;
    return matchPath({ path: route.path, end: true }, activePath) !== null;
  });

type ShouldDelayProjectRouteRenderInput = {
  activePath: string;
  authLoading: boolean;
  session: AuthSession | null;
  projectsLoaded: boolean;
  organisations: Array<Pick<OrganisationDto, 'id' | 'slug'>>;
  projects: Array<Pick<ProjectDto, 'id' | 'slug' | 'organisationId'>>;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  routeDefinitions: AppRouteDefinition[];
};

export const shouldDelayProjectRouteRenderForContext = ({
  activePath,
  authLoading,
  session,
  projectsLoaded,
  organisations,
  projects,
  selectedOrganisationId,
  selectedProjectId,
  routeDefinitions,
}: ShouldDelayProjectRouteRenderInput): boolean => {
  if (authLoading) return false;
  if (!session) return false;
  if (!projectsLoaded) return false;
  const isScopedProductRoute =
    resolveProjectRouteScope(activePath) !== null && isProjectScopedProductPath(activePath);
  const isDetailRoute = isDetailRoutePath(activePath, routeDefinitions);
  if (!isDetailRoute) {
    return false;
  }
  if (!isScopedProductRoute && selectedOrganisationId !== null && selectedProjectId === null) {
    return false;
  }

  const resolution = resolveActiveContext({
    pathname: activePath,
    session,
    organisations,
    projects,
    persistedOrganisationId: selectedOrganisationId,
    persistedProjectId: selectedProjectId,
  });
  const nextOrganisationId = resolution.activeOrganisationId;
  const nextProjectId = resolution.activeProjectId;
  if (nextOrganisationId === null && nextProjectId === null) return false;

  return nextOrganisationId !== selectedOrganisationId || nextProjectId !== selectedProjectId;
};
